/**
 * BrowserSTT - Browser-based Speech-to-Text using Web Speech API
 * 
 * Implements SpeechEngine interface using Web Speech API with:
 * - continuous: false (no auto-restart loops)
 * - interimResults: true (get partial results)
 * - State machine driven lifecycle
 */

import { SpeechEngine, SpeechStates } from './SpeechEngine.js';
import { SpeechStateMachine } from './SpeechStateMachine.js';

export class BrowserSTT extends SpeechEngine {
    static isSupported() {
        return 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
    }

    constructor(options = {}) {
        super();
        
        this.options = {
            language: options.language || 'en-US',
            maxAlternatives: options.maxAlternatives || 1,
            ...options
        };
        
        this.recognition = null;
        this.stateMachine = new SpeechStateMachine({
            maxRetries: options.maxRetries || 3,
            baseDelay: options.baseDelay || 1000,
            maxDelay: options.maxDelay || 10000
        });
        
        this.isSupported = false;
        this.partialBuffer = '';
        this.isManualStop = false;
        
        this.initializeRecognition();
        this.setupStateMachine();
    }

    /**
     * Initialize Web Speech API recognition
     */
    initializeRecognition() {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (!SpeechRecognition) {
            console.warn('Speech recognition not supported in this browser');
            this.isSupported = false;
            return;
        }

        this.isSupported = true;
        this.recognition = new SpeechRecognition();
        
        // Configure recognition settings (force continuous, interim results)
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.recognition.lang = this.options.language;
        this.recognition.maxAlternatives = this.options.maxAlternatives;
        
        this.setupEventHandlers();
    }

    /**
     * Setup state machine callbacks
     */
    setupStateMachine() {
        this.stateMachine.setOnStateChange((stateChange) => {
            if (this.onStateChange) {
                this.onStateChange(stateChange);
            }
        });
    }

    /**
     * Setup Web Speech API event handlers
     */
    setupEventHandlers() {
        if (!this.recognition) return;

        this.recognition.onstart = () => {
            if (process.env.NODE_ENV === 'development') console.log('🎤 BrowserSTT: Recognition started');
            this.stateMachine.transitionTo(SpeechStates.LISTENING, { source: 'onstart' });
        };

        this.recognition.onresult = (event) => {
            let finalTranscript = '';
            let interimTranscript = '';

            // Process all results
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                const transcript = result[0]?.transcript || '';

                if (result.isFinal) {
                    finalTranscript += transcript;
                } else {
                    interimTranscript += transcript;
                }
            }

            // Handle partial results
            if (interimTranscript.trim()) {
                this.partialBuffer = interimTranscript.trim();
                if (this.onPartial) {
                    this.onPartial({
                        transcript: this.partialBuffer,
                        confidence: event.results[event.results.length - 1]?.[0]?.confidence || 0,
                        isFinal: false
                    });
                }
            }

            // Handle final results
            if (finalTranscript.trim()) {
                const finalText = finalTranscript.trim();
                if (process.env.NODE_ENV === 'development') console.log('🎤 BrowserSTT: Final result:', finalText);
                
                if (this.onFinal) {
                    this.onFinal({
                        transcript: finalText,
                        confidence: event.results[event.results.length - 1]?.[0]?.confidence || 0,
                        isFinal: true
                    });
                }
                
                // Clear partial buffer
                this.partialBuffer = '';
            }
        };

        this.recognition.onerror = (event) => {
            if (process.env.NODE_ENV === 'development') console.error('🎤 BrowserSTT: Recognition error:', event.error);
            
            // Handle different error types
            switch (event.error) {
                case 'no-speech':
                    // No speech detected - this is normal, just endpoint
                    // Treat as normal end without surfacing error
                    this.stateMachine.transitionTo(SpeechStates.ENDPOINTED, { reason: 'no_speech' });
                    if (this.onEnd) { try { this.onEnd(); } catch (_) {} }
                    break;
                    
                case 'audio-capture':
                case 'not-allowed':
                case 'network':
                    // Serious errors - go to error state
                    this.stateMachine.transitionTo(SpeechStates.ERROR, { 
                        error: event.error, 
                        reason: 'serious_error' 
                    });
                    break;
                    
                case 'aborted':
                    // Manual stop - go to idle
                    this.stateMachine.transitionTo(SpeechStates.IDLE, { 
                        error: event.error, 
                        reason: 'manual_stop' 
                    });
                    break;
                    
                default:
                    // Other errors - try to recover
                    this.stateMachine.transitionTo(SpeechStates.ERROR, { 
                        error: event.error, 
                        reason: 'unknown_error' 
                    });
            }
            
            if (this.onError) {
                this.onError({
                    error: event.error,
                    message: this.getErrorMessage(event.error)
                });
            }
        };

        this.recognition.onend = () => {
            if (process.env.NODE_ENV === 'development') console.log('🎤 BrowserSTT: Recognition ended');
            
            // If we're still listening, transition to endpointed
            if (this.stateMachine.isInState(SpeechStates.LISTENING)) {
                this.stateMachine.transitionTo(SpeechStates.ENDPOINTED, { 
                    reason: 'recognition_ended' 
                });
            }
        };
    }

    /**
     * Start speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        if (!this.isSupported || !this.recognition) {
            console.error('🎤 BrowserSTT: Not supported or not initialized');
            return false;
        }

        if (this.stateMachine.isListening()) {
            if (process.env.NODE_ENV === 'development') console.log('🎤 BrowserSTT: Already listening');
            return true;
        }

        try {
            this.isManualStop = false;
            this.partialBuffer = '';
            this.stateMachine.resetRetryCount();
            
            this.stateMachine.transitionTo(SpeechStates.ACQUIRING_MIC, { 
                reason: 'start_requested' 
            });
            
            if (process.env.NODE_ENV === 'development') console.log('🎤 BrowserSTT: Starting recognition...');
            this.recognition.start();
            
            return true;
        } catch (error) {
            console.error('🎤 BrowserSTT: Failed to start:', error);
            
            // Handle start errors with retry logic
            const willRetry = this.stateMachine.handleError(error, () => {
                this.start();
            });
            
            if (!willRetry) {
                this.stateMachine.transitionTo(SpeechStates.ERROR, { 
                    error: error.message, 
                    reason: 'start_failed' 
                });
            }
            
            return false;
        }
    }

    /**
     * Stop speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        if (!this.recognition) {
            return false;
        }

        try {
            this.isManualStop = true;
            this.stateMachine.clearRetryTimeout();
            
            if (this.stateMachine.isListening()) {
                if (process.env.NODE_ENV === 'development') console.log('🎤 BrowserSTT: Stopping recognition...');
                this.recognition.stop();
            }
            
            // Only transition if we're not already idle
            if (this.stateMachine.getCurrentState() !== SpeechStates.IDLE) {
                this.stateMachine.transitionTo(SpeechStates.IDLE, { 
                    reason: 'manual_stop' 
                });
            }
            
            return true;
        } catch (error) {
            console.error('🎤 BrowserSTT: Failed to stop:', error);
            return false;
        }
    }

    /**
     * Check if currently listening
     * @returns {boolean} Listening status
     */
    isListening() {
        return this.stateMachine.isListening();
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getState() {
        return this.stateMachine.getCurrentState();
    }

    /**
     * Get error message for error type
     * @param {string} errorType - Error type
     * @returns {string} Error message
     */
    getErrorMessage(errorType) {
        const messages = {
            'no-speech': 'No speech detected',
            'audio-capture': 'Audio capture failed',
            'not-allowed': 'Microphone access denied',
            'network': 'Network error',
            'aborted': 'Recognition aborted',
            'language-not-supported': 'Language not supported'
        };
        
        return messages[errorType] || 'Unknown error occurred';
    }

    /**
     * Check if speech recognition is supported
     * @returns {boolean} Support status
     */
    isSupported() {
        return this.isSupported;
    }

    /**
     * Get browser compatibility info
     * @returns {Object} Compatibility info
     */
    getBrowserInfo() {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        const isEdge = /Edg/.test(navigator.userAgent);
        const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
        
        return {
            hasSpeechRecognition: !!SpeechRecognition,
            isChrome,
            isEdge,
            isSafari,
            userAgent: navigator.userAgent,
            isSupported: this.isSupported
        };
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.stop();
        this.stateMachine.destroy();
        this.recognition = null;
        super.destroy();
    }
}
